/**
 * UVP 主应用组件
 *
 * 职责：
 * 1. 全局路由配置（React Router v8）
 * 2. 权限守卫（RequireAuth）
 * 3. 主布局（MainLayout）
 *
 * 路由结构：
 * /login                    → 登录页（无需认证）
 * /                         → 主布局 + 统一仪表盘（需认证）
 * /comparison/api/endpoints            → API 端点注册
 * /comparison/api/rules/:endpointId    → API 比较规则配置
 * /comparison/api/runs/:runId          → API 比较运行详情
 *
 * @ref: master.md § 2 System Architecture
 * @ref: ui-designer.md § 2 Module-Specific Visual Design
 */

import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from 'react-router';
import { Spin, Result, Button } from 'antd';
import { Suspense, lazy } from 'react';

// ---- 布局组件 ----
import MainLayout from '@/components/layout/MainLayout';

// ---- 权限守卫 ----
import RequireAuth from '@/components/shared/RequireAuth';

// ---- 页面组件（路由懒加载，减少首屏体积）----
const LoginPage = lazy(() => import('@/features/auth/LoginPage'));
const EndpointsPage = lazy(() => import('@/features/api-comparison/EndpointsPage'));
const RulesPage = lazy(() => import('@/features/api-comparison/RulesPage'));
const ApiRunDetailPage = lazy(() => import('@/features/api-comparison/ApiRunDetailPage'));
const SuitesPage = lazy(() => import('@/features/api-comparison/SuitesPage'));
const SuiteDetailPage = lazy(() => import('@/features/api-comparison/SuiteDetailPage'));
const SuiteRunPage = lazy(() => import('@/features/api-comparison/SuiteRunPage'));
const TemplatesPage = lazy(() => import('@/features/api-comparison/TemplatesPage'));
// V1.1 新增页面
const WorkspaceListPage = lazy(() => import('@/features/workspace/WorkspaceListPage'));
const WorkspaceSettingsPage = lazy(() => import('@/features/workspace/WorkspaceSettingsPage'));
// V1.2 审批工作流页面
const PendingApprovalListPage = lazy(() => import('@/features/approval/PendingApprovalListPage'));
// 首页仪表盘
const UnifiedDashboardPage = lazy(() => import('@/features/dashboard/UnifiedDashboardPage'));
// Browser Request Capture（浏览器请求捕获）
const BrowserCapturePairPage = lazy(
  () => import('@/features/browser-capture/BrowserCapturePairPage')
);
const BrowserCaptureReviewPage = lazy(
  () => import('@/features/browser-capture/BrowserCaptureReviewPage')
);
const CommerceVerificationProfilePage = lazy(
  () => import('@/features/commerce-one-click/CommerceVerificationProfilePage')
);
const CommerceVerificationRunPage = lazy(
  () => import('@/features/commerce-one-click/CommerceVerificationRunPage')
);
// Replay 数据源管理（部署级管理员）
const ReplaySourceManagementPage = lazy(
  () => import('@/features/replay-sources/ReplaySourceManagementPage')
);

// ---- 全局加载状态（路由切换时显示）----
const PageLoader = () => <Spin size="large" tip="加载中..." fullscreen />;

const LegacyApiRulesRedirect = () => {
  const { endpointId } = useParams<{ endpointId: string }>();
  return <Navigate to={`/comparison/api/rules/${endpointId ?? ''}`} replace />;
};

const LegacyApiRunsRedirect = () => {
  const { runId } = useParams<{ runId: string }>();
  return <Navigate to={`/comparison/api/runs/${runId ?? ''}`} replace />;
};

const NotFoundPage = () => {
  const navigate = useNavigate();
  return (
    <Result
      status="404"
      title="404"
      subTitle="抱歉，你访问的页面不存在。"
      extra={
        <Button type="primary" onClick={() => navigate('/')}>
          返回首页
        </Button>
      }
    />
  );
};

function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* ========== 公开路由 ========== */}
          <Route path="/login" element={<LoginPage />} />

          {/* ========== 受保护路由（需认证）========== */}
          <Route
            path="/"
            element={
              <RequireAuth>
                <MainLayout />
              </RequireAuth>
            }
          >
            {/* 首页 → 统一仪表盘 */}
            <Route index element={<UnifiedDashboardPage />} />

            {/* API 比较模块 */}
            <Route path="comparison/api/endpoints" element={<EndpointsPage />} />
            <Route path="comparison/api/rules/:endpointId" element={<RulesPage />} />
            <Route path="comparison/api/runs/:runId" element={<ApiRunDetailPage />} />
            <Route path="comparison/api/suites" element={<SuitesPage />} />
            <Route path="comparison/api/suites/:suiteId" element={<SuiteDetailPage />} />
            <Route path="comparison/api/suite-runs/:suiteRunId" element={<SuiteRunPage />} />
            <Route path="comparison/api/templates" element={<TemplatesPage />} />
            <Route
              path="api/endpoints"
              element={<Navigate to="/comparison/api/endpoints" replace />}
            />
            <Route path="api/rules/:endpointId" element={<LegacyApiRulesRedirect />} />
            <Route path="api/runs/:runId" element={<LegacyApiRunsRedirect />} />

            {/* Replay 数据源管理（部署级管理员，HEI-4021） */}
            <Route path="settings/replay-sources" element={<ReplaySourceManagementPage />} />

            {/* V1.1 新增路由 */}
            <Route path="workspaces" element={<WorkspaceListPage />} />
            <Route path="workspaces/:workspaceId/settings" element={<WorkspaceSettingsPage />} />

            {/* V1.2 审批工作流 */}
            <Route path="workspaces/:workspaceId/approvals" element={<PendingApprovalListPage />} />

            {/* Browser Request Capture */}
            <Route path="settings/browser-extension/pair" element={<BrowserCapturePairPage />} />
            <Route path="browser-captures/:captureId" element={<BrowserCaptureReviewPage />} />
            <Route
              path="commerce/verification-profile"
              element={<CommerceVerificationProfilePage />}
            />
            <Route path="commerce/verifications" element={<CommerceVerificationRunPage />} />
            <Route
              path="verifications/:verificationId/report"
              element={<CommerceVerificationRunPage />}
            />

            {/* 已登录用户访问未匹配路由 → 侧边栏外壳内的 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>

          {/* 未登录用户访问未匹配路由 → 仍走 RequireAuth 原有的登录重定向 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
